import * as React from 'react';
import { useState } from 'react';

/** Material UI */
import { Box, Dialog, DialogContent } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DataGridPremium } from '@mui/x-data-grid-premium';

/** Redux Packages */
import { useDispatch } from 'react-redux';

/** Redux Reducers */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components */
import { callAPI } from 'shared/api/callApi';
import DragAndDrop from 'shared/ui/DragAndDrop/DragAndDrop';
import DialogHeader from 'shared/ui/DialogHeader';
import { PaperComponent } from 'shared/ui/DraggableDialog';
import { dutyEntryDetailGridSx } from 'shared/config/Styles';

import { getUploadItemColumns, isNegativeAmount, negativeValueMessage } from './DutyEntriesDef';
import { applyRecalculatedSiblings, flattenRow } from '../../lib/rowSerializers';
import { confirmSojuTierSelection } from '../../lib/sojuTier';
export default function UploadItem({
    dutyEntry,
    userEmail,
    getAccessTokenSilently,
    newOpen,
    setNewOpen,
    fileUploadLoading,
    setFileUploadLoading,
    setDutyEntryDetailRows,
    onEntryDetailsAdded,
    onEntryDetailsUpdated,
    setGridLoading,
}) {
    const dispatch = useDispatch();
    const [parsedRows, setParsedRows] = useState([]);

    const handleClose = () => {
        if (parsedRows.length > 0) {
            const isConfirmed = window.confirm('Are you sure you want to close this dialog?');
            if (isConfirmed) {
                setNewOpen(false);
                setParsedRows([]);
            } else {
                return;
            }
        } else {
            setNewOpen(false);
            setParsedRows([]);
        }
    };

    /**
     * Saves every line item parsed from the uploaded Excel files to this entry in one go.
     *
     * Rows that came straight from the file never passed the cell-level checks, so negative
     * amounts are caught here and named by line number. All rows are then checked together
     * against the year's Tier 1 limit — several rows can only cross it combined — and the
     * operator confirms or cancels; cancelling keeps the dialog and the rows as they are.
     */
    const handleSaveBtn = async () => {
        const negativeRowNumbers = parsedRows
            .map((row, index) =>
                isNegativeAmount(row.invoice_amount) || isNegativeAmount(row.quantity) ? index + 1 : null,
            )
            .filter(Boolean);
        if (negativeRowNumbers.length > 0) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: `${negativeValueMessage('Quantity or Invoice Amount')} (row ${negativeRowNumbers.join(', ')})`,
                    severity: 'error',
                }),
            );
            return;
        }

        setGridLoading(true);
        let canProceed;
        try {
            canProceed = await confirmSojuTierSelection({
                ilDutyEntryId: dutyEntry.id,
                details: parsedRows,
                getAccessTokenSilently,
                userEmail,
            });
        } finally {
            setGridLoading(false);
        }
        if (!canProceed) return;

        // source_file_id only ties preview rows to their uploaded file; keep it out of the API payload.
        const payload = parsedRows.map(({ source_file_id, ...row }) => ({
            ...row,
            il_duty_entry_id: dutyEntry.id,
        }));

        callAPI({
            method: 'post',
            module: 'duty_calculator',
            component: 'duty_entry_detail',
            type: 'multiple',
            getAccessTokenSilently,
            email: userEmail,
            obj: payload,
            onInit: () => setGridLoading(true),
            onFinal: () => setGridLoading(false),
            onSuccess: (resp) => {
                console.log('upload item response', resp);
                // A -CAN line changes its main item's duty — the server sends those main rows back recalculated
                const createdRows = (resp.data ?? []).map(({ recalculated_siblings, ...row }) => row);
                const recalculatedRows = (resp.data ?? []).flatMap((row) => row.recalculated_siblings ?? []);
                const flattenedRows = createdRows.map(flattenRow);
                setDutyEntryDetailRows((prev) =>
                    applyRecalculatedSiblings([...prev, ...flattenedRows], recalculatedRows),
                );
                onEntryDetailsAdded(dutyEntry.id, createdRows);
                recalculatedRows.forEach((sibling) => onEntryDetailsUpdated?.(dutyEntry.id, sibling));
                setNewOpen(false);
                setParsedRows([]);
                dispatch(setSnackbar({ open: true, message: 'Successfully Saved.', severity: 'success' }));
            },
            onError: (error) => {
                console.log('upload item error', error);
            },
        });
    };

    /** Applies an edit the operator made to a previewed row; negative amounts are refused, since a paste bypasses the cell check. */
    const processRowUpdate = (newRow, oldRow) => {
        const negativeField = ['quantity', 'invoice_amount'].find((field) => isNegativeAmount(newRow[field]));
        if (negativeField) {
            const label = negativeField === 'quantity' ? 'Quantity' : 'Invoice Amount';
            dispatch(setSnackbar({ open: true, message: negativeValueMessage(label), severity: 'error' }));
            return oldRow;
        }
        setParsedRows((prev) => prev.map((row) => (row.id === oldRow.id ? newRow : row)));
        return newRow;
    };

    return (
        <Dialog
            onClose={handleClose}
            open={newOpen}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { borderRadius: '18px' } }}
            PaperComponent={PaperComponent}
        >
            <DialogHeader
                cursor
                title="UPLOAD DUTY ITEM"
                onClose={handleClose}
                rightChildren={
                    <LoadingButton
                        onClick={handleSaveBtn}
                        loading={fileUploadLoading}
                        sx={{
                            color: '#00647e',
                            bgcolor: 'white',
                            width: '52px',
                            height: '30px',
                            borderRadius: '3px',
                            ':hover': { bgcolor: 'rgb(0,100,126)', color: 'white' },
                        }}
                    >
                        SAVE
                    </LoadingButton>
                }
            />
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, py: 3 }}>
                    <DragAndDrop
                        moduleName="duty_calculator"
                        componentName="duty_entry_detail"
                        setCalled={setParsedRows}
                        maxFileSize={26214400}
                        setFileUploadLoading={setFileUploadLoading}
                        fileExtensions={['pdf', 'xls', 'xlsx', 'doc', 'docx', 'png', 'jpg']}
                    />
                    <DataGridPremium
                        rows={parsedRows}
                        columns={getUploadItemColumns()}
                        density="compact"
                        editMode="cell"
                        processRowUpdate={processRowUpdate}
                        hideFooter
                        disableColumnMenu
                        disableColumnSorting
                        cellSelection
                        disableRowSelectionOnClick
                        getRowHeight={() => 'auto'}
                        sx={dutyEntryDetailGridSx}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    id: false,
                                },
                            },
                        }}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
}
